<!-- Cachebuster: 26 (used to clear cache on bit.dev, update if builds are not updated) -->
<template>
  <div class="selected-bonus">
    <div class="scroll-area">
      <ErrorMessage :message="error.message" :showError="error.show"></ErrorMessage>
      <div :class="{'loaded': headerImageLoaded, 'locked-bonus': bonusNeedsDeposit, 'bonus--unlocked': bonusUnlocked}" class="image" v-if="pendingBonus.ImageUrl">
        <img :src="pendingBonus.ImageUrl" @load="loaded" :class="{'loaded': headerImageLoaded}" />
      </div>
      <p class="date">{{expiryDateString}}</p>
      <h1 class="title" v-if="pendingBonus.Custom && pendingBonus.Custom.Title">{{pendingBonus.Custom.Title}}</h1>
      <div class="description wysiwyg" v-if="description" v-html="description"></div>
      <h4 class="terms-header" v-if="showTermsHeader">{{translations.pendingBonusesTermsAndConditionsHeader}}</h4>
      <div class="terms wysiwyg" v-html="termsAndConditionsAppended"></div>
    </div>
    <div class="buttons-row" v-if="ctaButtonText">
      <a v-if="this.bonusNeedsDeposit" :href="depositPageLink" class="cta-button">{{ctaButtonText}}</a>
      <button :disabled="isLoading" v-if="!this.bonusNeedsDeposit" class="cta-button" @click="consumeBonus">{{ctaButtonText}}</button>
      <Spinner v-if="isLoading"></Spinner>
      <div class="scroll-gradient"></div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Spinner from '../Spinner/Spinner.vue';
import ErrorMessage from '../ErrorMessage.vue';
import { consumePendingBonus } from '../../api/rewards';
import { formatDate, convertToISO } from '../../utils/date';
import { logWarn } from '../../utils/log';

export default {
  name: 'PendingBonus',
  components: {
    ErrorMessage,
    Spinner,
  },
  props: {
    authToken: {
      type: String,
    },
    fusionUrl: {
      type: String,
    },
    prismaUrl: {
      type: String,
    },
    pendingBonus: {
      type: Object,
    },
    translations: {
      type: Object,
    },
    mailtoLink: {
      type: String,
    },
    termsAboveButtons: {
      type: Boolean,
    },
    expiryDateString: {
      type: String,
    },
    depositPageUrl: {
      type: String,
    },
    showTermsHeader: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      headerImageLoaded: false,
      isLoading: false,
      error: {
        message: null,
        show: false,
      },
    };
  },
  methods: {
    consumeBonus() {
      this.isLoading = true;
      consumePendingBonus(this.authToken, this.prismaUrl, this.pendingBonus.Id, this.fusionUrl)
        .then((response) => {
          if (!response.data.Success) {
            if (response.data.Errors && response.data.Errors.length > 0) {
              this.error.message = response.data.Errors[0].Message;
              this.error.show = true;
              this.isLoading = false;
              setTimeout(() => {
                this.error.show = false;
              }, 3000);
            }
            return;
          }

          window.location = this.successfulClaimRedirectUrl;
        }).catch((error) => {
          logWarn(error);
          this.isLoading = false;
        });
    },
    formatDateString(dateStr) {
      return formatDate(convertToISO(dateStr));
    },
    loaded() {
      this.headerImageLoaded = true;
    },
  },
  computed: {
    ...mapGetters({
      isMobile: 'isMobile',
    }),
    showUnlockedText() {
      if (this.pendingBonus.Type === 1) return false;
      if (this.pendingBonus.Type === 2) return false;
      if (this.pendingBonus.Type === 3 && this.pendingBonus.IsLocked === 1) return false;
      return true;
    },
    bonusUnlocked() {
      return (this.pendingBonus.Type === 3 && this.pendingBonus.IsLocked === 0);
    },
    bonusNeedsDeposit() {
      return this.pendingBonus.Type === 2 || (this.pendingBonus.Type === 3 && this.pendingBonus.IsLocked === 1);
    },
    description() {
      if (this.showUnlockedText) {
        return this.translations.pendingBonusesBonusUnlockedText;
      }

      return this.pendingBonus.Description || '';
    },
    ctaButtonText() {
      if (this.bonusNeedsDeposit) {
        return this.translations.pendingBonusesCTAUnlockText;
      }

      return (this.pendingBonus.Custom && this.pendingBonus.Custom.CtaText) ? this.pendingBonus.Custom.CtaText : this.translations.pendingBonusesCTAClaimFallbackText;
    },
    successfulClaimRedirectUrl() {
      if (this.pendingBonus.Custom && this.pendingBonus.Custom.CtaLink) return this.pendingBonus.Custom.CtaLink;
      if (this.pendingBonus.DesktopGameUrl && this.pendingBonus.MobileGameUrl) {
        if (this.isMobile) return this.pendingBonus.MobileGameUrl;
        if (!this.isMobile) return this.pendingBonus.DesktopGameUrl;
      }
      return '';
    },
    depositPageLink() {
      let url = this.depositPageUrl;
      url = url.replace('[[bonusid]]', this.pendingBonus.Id);
      url = url.replace('[[bonuscode]]', this.pendingBonus.BonusCode);
      url = url.replace('[[unlockamount]]', this.pendingBonus.UnlockAmount);
      return url;
    },
    termsAndConditionsAppended() {
      if (this.pendingBonus.TermsAndConditions && this.expiryDateString !== '') {
        return `<span class='terms-and-conditions-expiry-date'>${this.expiryDateString}.</span> ${this.pendingBonus.TermsAndConditions}`;
      }
      if (this.pendingBonus.TermsAndConditions) {
        return this.pendingBonus.TermsAndConditions;
      }
      if (this.expiryDateString !== '') {
        return `<span class='terms-and-conditions-expiry-date'>${this.expiryDateString}.</span>`;
      }
      return '';
    },
  },
};
</script>

<style lang="less">
#fasttrack-crm {
  .selected-bonus {
    font-family: "Lato", sans-serif;
    width: 100%;
    max-width: 100%;
    min-height: 40vh;
    height: 100%;
    max-height: 100%;
    background-color: #fff;
    position: relative;
    transition: 300ms;
    transition: 10s;
    transition: all 250ms ease-in-out;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-radius: 0px 0px 6px 6px;

    .scroll-area {
      overflow-y: auto;
      flex-grow: 1;
      padding-bottom: 50px;
    }

    .image {
      width: 100%;
      transition: all 300ms ease;
      background-color: #eeeeee;

      img {
        width: 100%;
        height: auto;
        display: block;
        opacity: 0;
        transition: all 300ms ease;

        &.loaded {
          opacity: 1;
        }
      }
    }

    .date {
      font-family: "Lato", sans-serif;
      font-size: 10px;
      display: block;
      margin: 10px 25px 0px 25px;
      text-align: center;
      color: rgba(51, 51, 51, 0.6);
    }

    .title {
      font-family: "Lato", sans-serif;
      color: #000;
      font-size: 28px;
      line-height: 28px;
      font-weight: 800;
      display: block;
      margin: 5px 25px 10px 25px;
      text-align: center;
    }

    .description {
      font-family: "Lato", sans-serif;
      color: #000;
      font-size: 14px;
      font-weight: 300;
      display: block;
      margin: 5px 25px 10px 25px;
      text-align: center;
    }

    .wysiwyg {
      * {
        font-family: "Lato", sans-serif;
      }
      ul {
        li {
          text-align: left;
        }
      }

      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        font-family: "Lato", sans-serif;
        color: #000;
        font-weight: 600;
        margin-bottom: 0;
        padding: 0;
      }

      h2 {
        font-size: 21px;
      }
      h3 {
        font-size: 18px;
      }
      h4 {
        font-size: 16px;
      }
      h5 {
        font-size: 12px;
      }
      h6 {
        font-size: 12px;
      }
    }

    .terms-header {
      text-align: center;
      margin: 0;
      font-size: 12px;
    }

    .terms {
      margin: 0px 25px 10px 25px;
      font-size: 10px;
      line-height: 1.4;
      color: #333;
      text-align: center;

      a {
        color: #2d72ee;
        font-weight: 700;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }

      b {
        font-weight: 700;
      }
    }

    .buttons-row {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      position: relative;
    }

    .scroll-gradient {
      position: absolute;
      left: 0;
      right: 0;
      top: -40px;
      height: 40px;
      width: 100%;

      /* Permalink - use to edit and share this gradient: https://colorzilla.com/gradient-editor/#ffffff+0,ffffff+100&0+0,1+100 */
      background: -moz-linear-gradient(
        top,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 1) 100%
      ); /* FF3.6-15 */
      background: -webkit-linear-gradient(
        top,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 1) 100%
      ); /* Chrome10-25,Safari5.1-6 */
      background: linear-gradient(
        to bottom,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 1) 100%
      ); /* W3C, IE10+, FF16+, Chrome26+, Opera12+, Safari7+ */
      filter: progid:DXImageTransform.Microsoft.gradient( startColorstr='#00ffffff', endColorstr='#ffffff',GradientType=0 ); /* IE6-9 */
    }

    .cta-button {
      font-family: "Lato", sans-serif;
      cursor: pointer;
      text-transform: uppercase;
      font-size: 14px;
      font-weight: 700;
      background-color: #0e9543;
      color: white;
      border-radius: 4px;
      box-shadow: -2px -2px 0px inset rgba(0, 0, 0, 0.3);
      display: inline-block;
      padding: 15px 15px;
      text-decoration: none;
      outline: none;
      border: 0;
      margin: 15px;
      position: relative;
      text-align: center;
      min-width: 130px;
      -webkit-font-smoothing: antialiased;

      &.secondary {
        color: #000;
        background-color: #f6cd34;
      }

      &.loading {
        background-color: #d7d7d7;
      }

      &:hover {
        &:before {
          background-color: rgba(0, 0, 0, 0.15);
        }
      }

      &:before {
        content: "";
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
        border-radius: 4px;
        transition: all 0.2s linear;
      }
    }
  }
}
</style>
